import React from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Target, ArrowRight, ChevronLeft, CheckCircle2, BarChart3, LineChart, ChevronRight } from 'lucide-react';
import { useSound } from '../hooks/useSound';

const caseStudies = [
  {
    id: 'manufacturing-excellence',
    company: 'Manufacturing Excellence Corp.',
    industry: 'Manufacturing',
    icon: TrendingUp,
    challenge: 'Operational inefficiency and lack of strategic growth planning.',
    solution: 'AI-driven process improvement, resource allocation, and quality control.',
    results: [
      '20% efficiency boost',
      '35% cost reduction',
      '18% revenue growth'
    ],
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&h=400',
    fullCaseStudy: {
      challenge: {
        main: 'Manufacturing Excellence Corp., a mid-sized manufacturing company, faced challenges with operational inefficiency, inconsistent quality control, and difficulty scaling production while maintaining cost efficiency. Leadership struggled to identify performance bottlenecks and implement long-term strategic planning.',
        details: [
          'Inconsistent production quality leading to increased waste',
          'Inefficient resource allocation across production lines',
          'Lack of data-driven decision making processes',
          'Limited visibility into performance metrics'
        ]
      },
      solution: {
        main: 'ProfitIQ.AI implemented an AI-driven optimization strategy that transformed their operations:',
        phases: [
          {
            title: 'Analysis & Assessment',
            description: 'Comprehensive analysis of production workflows to identify inefficiencies',
            icon: BarChart3
          },
          {
            title: 'Resource Optimization',
            description: 'AI-powered resource allocation system for maximum efficiency',
            icon: Target
          },
          {
            title: 'Quality Enhancement',
            description: 'Implementation of AI-based quality control measures',
            icon: CheckCircle2
          },
          {
            title: 'Strategic Planning',
            description: 'Development of data-driven growth strategy',
            icon: LineChart
          }
        ]
      },
      results: {
        main: "The implementation of ProfitIQ.AI's solutions led to significant improvements across all key metrics:",
        metrics: [
          {
            label: 'Operational Efficiency',
            value: '20%',
            description: 'Increase in overall operational efficiency'
          },
          {
            label: 'Cost Reduction',
            value: '35%',
            description: 'Reduction in production costs'
          },
          {
            label: 'Revenue Growth',
            value: '18%',
            description: 'Increase in revenue within 12 months'
          },
          {
            label: 'Quality Improvement',
            value: '25%',
            description: 'Reduction in product defects'
          }
        ]
      }
    }
  },
  {
    id: 'retail-innovation',
    company: 'Retail Innovation Leaders',
    industry: 'Retail',
    icon: Users,
    challenge: 'Rising competition, supply chain disruptions, and inefficient inventory management.',
    solution: 'Customer experience enhancement, inventory optimization, and streamlined operations.',
    results: [
      '15% higher customer retention',
      '30% increase in operational efficiency',
      '10% revenue growth'
    ],
    image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&w=800&h=400',
    fullCaseStudy: {
      challenge: {
        main: 'Retail Innovation Leaders, a growing retail business, struggled with rising competition, supply chain disruptions, and inefficient inventory management. The company needed to improve its market positioning while optimizing its operational processes to drive profitability.',
        details: [
          'Rising competition in the retail space',
          'Supply chain disruptions affecting inventory',
          'Inefficient inventory management',
          'Need for improved market positioning'
        ]
      },
      solution: {
        main: 'ProfitIQ.AI developed a comprehensive strategy that included:',
        phases: [
          {
            title: 'Customer Experience',
            description: 'AI-powered customer experience enhancements to improve engagement',
            icon: Users
          },
          {
            title: 'Inventory Management',
            description: 'Smart inventory optimization to reduce waste',
            icon: BarChart3
          },
          {
            title: 'Operational Efficiency',
            description: 'Streamlined workflows for improved productivity',
            icon: Target
          },
          {
            title: 'Market Strategy',
            description: 'Refined pricing and promotional strategies',
            icon: LineChart
          }
        ]
      },
      results: {
        main: "The implementation of ProfitIQ.AI's solutions led to significant improvements:",
        metrics: [
          {
            label: 'Customer Retention',
            value: '15%',
            description: 'Improvement in customer retention and repeat sales'
          },
          {
            label: 'Operational Efficiency',
            value: '30%',
            description: 'Increase in operational efficiency'
          },
          {
            label: 'Revenue Growth',
            value: '10%',
            description: 'Growth in revenue within first six months'
          },
          {
            label: 'Inventory Optimization',
            value: '40%',
            description: 'Reduction in excess stock levels'
          }
        ]
      }
    }
  },
  {
    id: 'professional-services',
    company: 'Professional Services Group',
    industry: 'Services',
    icon: Target,
    challenge: 'Fragmented client management and inconsistent service delivery.',
    solution: 'AI-powered service standardization and client relationship optimization.',
    results: [
      '25% improvement in client satisfaction',
      '40% reduction in response time',
      '22% increase in recurring revenue'
    ],
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&h=400',
    fullCaseStudy: {
      challenge: {
        main: 'Professional Services Group, a consulting firm, was struggling with client acquisition, inconsistent revenue streams, and difficulty in scaling its service offerings. The company needed a structured approach to business growth and a more effective sales and marketing strategy.',
        details: [
          'Inconsistent client acquisition and retention rates',
          'Fragmented service delivery processes',
          'Limited scalability of service offerings',
          'Inefficient sales and marketing approaches'
        ]
      },
      solution: {
        main: 'ProfitIQ.AI implemented a comprehensive transformation strategy:',
        phases: [
          {
            title: 'Client Analytics',
            description: 'AI-driven lead generation and client segmentation',
            icon: Users
          },
          {
            title: 'Service Optimization',
            description: 'Data-backed service package and pricing optimization',
            icon: BarChart3
          },
          {
            title: 'Automation',
            description: 'Automated client engagement and follow-up systems',
            icon: Target
          },
          {
            title: 'Strategic Planning',
            description: 'Revenue forecasting and scalability planning',
            icon: LineChart
          }
        ]
      },
      results: {
        main: "The implementation of our solutions delivered significant improvements across key performance indicators:",
        metrics: [
          {
            label: 'Client Acquisition',
            value: '25%',
            description: 'Increase in new client acquisition rate'
          },
          {
            label: 'Revenue Growth',
            value: '20%',
            description: 'Boost in revenue within six months'
          },
          {
            label: 'Client Retention',
            value: '30%',
            description: 'Improvement in repeat business rate'
          },
          {
            label: 'Operational Efficiency',
            value: '15%',
            description: 'Reduction in operational workload'
          }
        ]
      }
    }
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

const CaseStudyDetail = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();
  const { id } = useParams();
  
  const study = caseStudies.find(s => s.id === id);
  
  if (!study || !study.fullCaseStudy) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Case Study Not Found</h2>
        <motion.button
          whileHover={{ x: -4 }}
          onClick={() => navigate('/case-studies')}
          className="text-amber-400 hover:text-amber-300 transition-colors duration-300"
        >
          Return to Case Studies
        </motion.button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ x: -4 }}
        onClick={() => navigate('/case-studies')}
        onMouseEnter={playHover}
        className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors duration-300"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Case Studies
      </motion.button>

      <div className="relative h-80 rounded-xl overflow-hidden">
        <img
          src={study.image}
          alt={study.company}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 to-background/50" />
        <div className="absolute bottom-0 left-0 p-8">
          <study.icon className="w-12 h-12 text-amber-400 mb-4" />
          <h1 className="text-4xl font-bold text-white mb-2">{study.company}</h1>
          <p className="text-amber-400 text-xl">{study.industry}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, ...springConfig }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Challenge</h2>
            <p className="text-gray-400 mb-4">{study.fullCaseStudy.challenge.main}</p>
            <ul className="space-y-2">
              {study.fullCaseStudy.challenge.details.map((detail, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, ...springConfig }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Solution</h2>
            <p className="text-gray-400 mb-6">{study.fullCaseStudy.solution.main}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {study.fullCaseStudy.solution.phases.map((phase, idx) => (
                <motion.div
                  key={phase.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + (idx * 0.1), ...springConfig }}
                  className="p-4 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10"
                >
                  <phase.icon className="w-6 h-6 text-amber-400 mb-2" />
                  <h3 className="font-semibold text-white mb-1">{phase.title}</h3>
                  <p className="text-sm text-gray-400">{phase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, ...springConfig }}
      >
        <h2 className="text-2xl font-bold text-white mb-6">Results</h2>
        <p className="text-gray-400 mb-8">{study.fullCaseStudy.results.main}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {study.fullCaseStudy.results.metrics.map((metric, idx) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + (idx * 0.1), ...springConfig }}
              className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 text-center"
            >
              <div className="text-4xl font-bold text-amber-400 mb-2">{metric.value}</div>
              <div className="font-semibold text-white mb-2">{metric.label}</div>
              <p className="text-sm text-gray-400">{metric.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, ...springConfig }}
        className="text-center p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
      >
        <h2 className="text-2xl font-bold mb-4">
          <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
            Transform Your Business Today
          </span>
        </h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
          Join hundreds of successful businesses that have accelerated their growth with ProfitIQ.AI
        </p>
        <motion.button
          className="group relative px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg overflow-hidden shadow-lg shadow-amber-400/20 hover:shadow-amber-400/30"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
          onMouseEnter={playHover}
          onClick={() => {
            playClick();
            navigate('/consultation');
          }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-500"
            initial={{ x: "100%" }}
            whileHover={{ x: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          />
          <span className="relative z-10 flex items-center justify-center gap-2">
            Get Started
            <ChevronRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1" />
          </span>
        </motion.button>
      </motion.div>
    </div>
  );
};

export const CaseStudies = () => {
  const { playHover, playClick } = useSound();
  const navigate = useNavigate();

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ...springConfig }}
                  className="text-center mb-16"
                >
                  <h1 className="text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                      Real Business Transformations
                    </span>
                  </h1>
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                    Discover how businesses like yours achieved remarkable growth with ProfitIQ.AI
                  </p>
                </motion.div>
                
                <div className="space-y-12">
                  {caseStudies.map((study, index) => (
                    <motion.div
                      key={study.company}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
                    >
                      <motion.div
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ duration: 0.8, ...springConfig }}
                        className="overflow-hidden bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
                      >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div className="relative h-64 lg:h-auto">
                            <img
                              src={study.image}
                              alt={study.company}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-background/20" />
                            <div className="absolute bottom-0 left-0 p-6">
                              <study.icon className="w-8 h-8 text-amber-400 mb-2" />
                              <h3 className="text-2xl font-bold text-white">{study.company}</h3>
                              <p className="text-amber-400">{study.industry}</p>
                            </div>
                          </div>
                          <div className="p-6 lg:pr-8">
                            <div className="space-y-6">
                              <div>
                                <h4 className="text-lg font-semibold text-white mb-2">Challenge</h4>
                                <p className="text-gray-400">{study.challenge}</p>
                              </div>
                              <div>
                                <h4 className="text-lg font-semibold text-white mb-2">Solution</h4>
                                <p className="text-gray-400">{study.solution}</p>
                              </div>
                              <div>
                                <h4 className="text-lg font-semibold text-white mb-2">Results</h4>
                                <ul className="space-y-2">
                                  {study.results.map((result, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                      {result}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <motion.button
                                whileHover={{ x: 4 }}
                                whileTap={{ scale: 0.98 }}
                                transition={springConfig}
                                onMouseEnter={playHover}
                                onClick={() => {
                                  playClick();
                                  navigate(`/case-studies/${study.id}`);
                                }}
                                className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors duration-300"
                              >
                                Read Full Case Study
                                <ArrowRight className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 1, ...springConfig }}
                  className="mt-16 text-center p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
                >
                  <h2 className="text-2xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                      Ready to Transform Your Business?
                    </span>
                  </h2>
                  <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                    Join hundreds of successful businesses that have accelerated their growth with ProfitIQ.AI
                  </p>
                  <motion.button
                    className="group relative px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg overflow-hidden shadow-lg shadow-amber-400/20 hover:shadow-amber-400/30"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                    onMouseEnter={playHover}
                    onClick={() => {
                      playClick();
                      navigate('/consultation');
                    }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-500"
                      initial={{ x: "100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      Get Started
                      <ChevronRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1" />
                    </span>
                  </motion.button>
                </motion.div>
              </>
            }
          />
          <Route path=":id" element={<CaseStudyDetail />} />
        </Routes>
      </div>
    </div>
  );
};